import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Users, Star, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { PageHeader, EmptyState, Loading } from "@/components/dash/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const empty = { name: "", email: "", phone: "", notes: "", loyalty_points: "" };

export default function Customers() {
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [q, setQ] = useState("");

  const load = useCallback(() => api.get("/customers").then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/customers", { ...form, loyalty_points: parseInt(form.loyalty_points) || 0 });
      toast.success("Customer added"); setForm(empty); setOpen(false); load();
    } catch (err) { toast.error(apiErr(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this customer?")) return;
    await api.delete(`/customers/${id}`); toast.success("Deleted"); load();
  };

  if (!list) return <Loading />;
  const filtered = list.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()) || (c.email || "").includes(q));

  return (
    <div data-testid="customers-page">
      <PageHeader title="Customers" subtitle="Build loyalty with guest profiles and reward points."
        action={<Button data-testid="add-customer-btn" onClick={() => setOpen(true)} className="bg-primary hover:bg-[#B35A42] text-primary-foreground"><Plus className="h-4 w-4 mr-1" /> Add customer</Button>} />

      {list.length > 0 && (
        <Input data-testid="customer-search" placeholder="Search customers..." value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm mb-6" />
      )}

      {list.length === 0 ? (
        <EmptyState icon={Users} title="No customers yet" desc="Add guests to start building your customer database."
          action={<Button onClick={() => setOpen(true)} className="bg-primary text-primary-foreground"><Plus className="h-4 w-4 mr-1" /> Add customer</Button>} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((c) => (
            <div key={c.id} data-testid={`customer-${c.id}`} className="bg-card border border-border rounded-xl p-5">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <span className="h-10 w-10 rounded-full bg-accent/60 flex items-center justify-center font-heading font-semibold">{c.name.charAt(0)}</span>
                  <div>
                    <p className="font-medium">{c.name}</p>
                    <p className="text-sm text-primary flex items-center gap-1"><Star className="h-3.5 w-3.5 fill-primary" /> {c.loyalty_points} pts</p>
                  </div>
                </div>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => remove(c.id)} data-testid={`delete-customer-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
              <div className="mt-4 space-y-1.5 text-sm text-muted-foreground">
                {c.email && <p className="flex items-center gap-2"><Mail className="h-3.5 w-3.5" /> {c.email}</p>}
                {c.phone && <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" /> {c.phone}</p>}
                {c.notes && <p className="italic">"{c.notes}"</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Add customer</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-1.5"><Label>Name</Label>
              <Input data-testid="cust-form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Loyalty points</Label>
              <Input type="number" value={form.loyalty_points} onChange={(e) => setForm({ ...form, loyalty_points: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Allergies, preferences..." /></div>
            <DialogFooter>
              <Button type="submit" data-testid="cust-form-submit" className="bg-primary hover:bg-[#B35A42] text-primary-foreground">Add customer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
